import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { g, p as post } from "@/lib/api";
import { Receipt, Wifi, HardDrive, Info, Download } from "lucide-react";

/** Cassa fiscale: si scegle la marca e il gestionale prepara lo scontrino nel suo formato. */
export default function CassaFiscale() {
  const [marche, setMarche] = useState([]);
  const [c, setC] = useState({ marca: "nessuna", ip: "", porta: 0, cartella: "", lotteria: true, attiva: false });
  const [prova, setProva] = useState(null);

  useEffect(() => {
    g("/fiscale/marche").then((d) => setMarche(d.marche)).catch(() => {});
    g("/fiscale/impostazioni").then(setC).catch(() => {});
  }, []);

  const scelta = marche.find((m) => m.id === c.marca);

  const salva = async () => {
    try {
      const d = await post("/fiscale/impostazioni", { ...c, porta: Number(c.porta) || 0 });
      setC(d);
      toast.success("Cassa salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Non è stato possibile salvare");
    }
  };

  return (
    <div className="space-y-6" data-testid="cassa-fiscale-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-amber-600">Scontrino fiscale</p>
        <h1 className="font-head mt-1 flex items-center gap-2 text-3xl font-bold sm:text-4xl">
          <Receipt className="h-7 w-7 text-amber-500" /> La tua cassa
        </h1>
        <p className="mt-2 max-w-2xl text-base text-slate-600">
          Scegli il registratore che hai in cassa: il gestionale preparerà lo scontrino già nel suo
          formato, con le aliquote giuste e i pagamenti divisi tra contanti e carta.
        </p>
      </div>

      <Card className="space-y-5 p-5" data-testid="cassa-form">
        <label className="block space-y-1.5 text-sm">
          <span className="font-semibold text-slate-700">Marca e modello</span>
          <select
            className="h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={c.marca}
            onChange={(e) => {
              const m = marche.find((x) => x.id === e.target.value);
              setC({ ...c, marca: e.target.value, porta: m?.porta || 0 });
            }}
            data-testid="select-marca"
          >
            {marche.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>

        {scelta && (
          <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="cassa-nota">
            {scelta.modo === "rete" ? <Wifi className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              : <HardDrive className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
            <p className="text-sm text-slate-700">{scelta.note}</p>
          </div>
        )}

        {scelta?.modo === "rete" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5 text-sm">
              <span className="font-semibold text-slate-700">Indirizzo della cassa in rete</span>
              <Input className="h-12" placeholder="192.168.1.50" value={c.ip}
                     onChange={(e) => setC({ ...c, ip: e.target.value })} data-testid="input-ip" />
            </label>
            <label className="space-y-1.5 text-sm">
              <span className="font-semibold text-slate-700">Porta</span>
              <Input className="h-12" type="number" value={c.porta}
                     onChange={(e) => setC({ ...c, porta: e.target.value })} data-testid="input-porta" />
            </label>
          </div>
        )}

        {scelta?.modo === "file" && (
          <label className="block space-y-1.5 text-sm">
            <span className="font-semibold text-slate-700">Cartella controllata dal programma di cassa</span>
            <Input className="h-12" placeholder="C:\\WinEcrCom\\SPOOL" value={c.cartella}
                   onChange={(e) => setC({ ...c, cartella: e.target.value })} data-testid="input-cartella" />
            <span className="block text-xs text-slate-500">
              Te la dice il tecnico che ha installato la cassa. Il file dello scontrino si scarica dalla
              cassa del gestionale e va salvato in questa cartella: il programma lo prende e stampa.
            </span>
          </label>
        )}

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" className="h-4 w-4" checked={c.attiva}
                   onChange={(e) => setC({ ...c, attiva: e.target.checked })} data-testid="check-attiva" />
            Emetti lo scontrino quando incasso
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" className="h-4 w-4" checked={c.lotteria}
                   onChange={(e) => setC({ ...c, lotteria: e.target.checked })} data-testid="check-lotteria" />
            Chiedi il codice lotteria degli scontrini
          </label>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button className="h-14 px-8 text-base font-bold" onClick={salva} data-testid="salva-cassa">
            Salva la cassa
          </Button>
          <Button
            variant="outline"
            className="h-14 px-6 text-base"
            data-testid="prova-scontrino"
            onClick={async () => {
              try {
                const ordini = await g("/orders?status=chiuso");
                const ultimo = (Array.isArray(ordini) ? ordini : ordini.orders || [])[0];
                if (!ultimo) return toast.error("Serve un conto chiuso per fare la prova");
                setProva(await post(`/fiscale/scontrino/${ultimo.id}`, {}));
                toast.success("Scontrino preparato: controlla qui sotto");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Prova non riuscita");
              }
            }}
          >
            Prova con l'ultimo conto
          </Button>
        </div>
      </Card>

      {prova && (
        <Card className="space-y-3 p-5" data-testid="cassa-prova">
          <h2 className="font-head flex items-center gap-2 text-base font-bold md:text-lg">
            <Info className="h-4 w-4 text-amber-500" /> Scontrino da {prova.totale.toFixed(2)} € · {prova.marca_nome}
          </h2>
          <pre className="max-h-64 overflow-auto rounded-lg bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-200">
            {prova.contenuto}
          </pre>
          <div className="flex flex-wrap gap-2">
            {prova.url && (
              <Button
                className="h-12"
                data-testid="invia-cassa"
                onClick={async () => {
                  try {
                    await fetch(prova.url, { method: "POST", body: prova.contenuto,
                                             headers: { "Content-Type": prova.content_type } });
                    toast.success("Inviato alla cassa");
                  } catch {
                    toast.error("La cassa non risponde: controlla che il tablet sia sulla stessa rete");
                  }
                }}
              >
                Invia alla cassa ({prova.url})
              </Button>
            )}
            <Button
              variant="outline"
              className="h-12"
              data-testid="scarica-scontrino"
              onClick={() => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(new Blob([prova.contenuto], { type: prova.content_type }));
                a.download = prova.filename;
                a.click();
              }}
            >
              <Download className="mr-2 h-4 w-4" /> Scarica il file per il programma di cassa
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
